import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Container,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Categories } from "./categories.jsx";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
const FAQ_DATA = {
  gettingstartwithtipkit: [
    {
      q: "How Tipkit works for your team?",
      a: "Choose your model:\nB2B vs B2C\n\n B2B \n\n• Tips are collected through your hotel or business’s Stripe account\n• Guests can leave pooled/team or direct tips\n• Management verifies pooled tips and reviews all transactions in the dashboard\n• Tip reports are downloaded and assigned through payroll\n\nB2C\n\n• Each staff member connects their own Stripe account for direct payouts\n• Guests can leave direct or pooled tips (depending on setup)\n• No management action needed for direct tips — they go straight to the staff’s account\n• Tip data is still visible to management for tracking and transparency\n• Best for teams that want automatic payouts and minimal admin involvement",
    },
    {
      q: "How does digital tipping work for hotels?",
      a: "Guests scan a Tipkit QR code that can be placed anywhere in their room, at the valet stand, or during a service to leave a tip using their phone — no app required.",
    },
    {
      q: "How do guests leave a tip without an app?",
      a: "Guests simply scan the QR code with their phone camera. A browser page opens instantly where they can choose a tip amount, add a short thank-you message, and pay via Apple Pay, Google Pay, or card.",
    },
    {
      q: "How do managers track staff performance through Tipkit?",
      a: "Managers can access real-time analytics in the Tipkit dashboard — including tip frequency, total amounts, and guest feedback — to identify top performers and overall satisfaction trends.",
    },
    {
      q: "How easy is tipkit to use for staff members?",
      a: "Receiving tips through tipkit is simple! \n Staff have the option to check in through their app OR staff members can leave their QR code for the guest.",
    },
  ],
  tippingandpayments: [
    {
      q: "Can guests tip digitally if they don’t have cash?",
      a: "Yes. Tipkit is built for contactless, cashless tipping — guests can use any major card or digital wallet to leave a tip anytime.",
    },
    {
      q: "Are digital tips taxed?",
      a: "Currently, digital tips are federally tax-exempt for employees earning under $250,000 per year, though the IRS is still expected to release formal guidance. Employees still need to report their tips to their employer, but those earnings don’t count toward taxable income at the federal level. Employers, however, are still responsible for accurately tracking and reporting all tip income.",
    },
    {
      q: "How fast are tips distributed?",
      a: "B2B vs. B2C \n\n B2B\n\n• Tips are held in the entity’s Stripe account until payroll processing.\n• Payout timing depends on internal payroll schedules (e.g., weekly or biweekly).\n• Management has full control over verification and distribution.\n\nB2C\n\n• Tips are instantly processed through Stripe once verified.\n• Funds are typically available within 1–2 business days in the staff member’s bank account.\n• Staff can view and track their incoming tips in real time.",
    },
  ],
  hotelandstaffguide: [
    {
      q: "How to get more tips as a hotel housekeeper or valet?",
      a: "Personal touches matter; a warm greeting, remembering guest preferences, and leaving a short thank-you note can increase the likelihood of receiving digital tips.",
    },
    {
      q: "What makes a guest more likely to tip again?",
      a: "Guests tend to tip again when they receive consistent, attentive service and an easy way to show appreciation. Tipkit’s one-scan experience removes friction and encourages repeat tipping.",
    },
    {
      q: "Can I track my tips by day?",
      a: "Yes. Staff can log in to their Tipkit profile to view daily, weekly, or monthly earnings and see their performance trends over time.",
    },
  ],
  digitaltiping: [
    {
      q: "What is digital tipping?",
      a: "Digital tipping allows guests to leave cashless tips through a QR code or link — ensuring staff are rewarded even when guests don’t carry cash.\n\nOr\n\nDigital tipping lets guests show appreciation without needing cash. Using a QR code or link, guests can leave a tip straight from their phone — quick, secure, and cash-free.\n\n Behind the scenes, Tipkit makes the process seamless for hotels and staff, ensuring every tip is tracked. By replacing traditional cash envelopes, digital tipping brings transparency, efficiency, and convenience to both guests and employees.",
    },
  ],
  benefits: [
    {
      q: "How tipkit helps tackle reduced staff turnover?",
      a: " When staff feel recognized and fairly compensated, they’re far more likely to stay. Tipkit’s digital tipping system helps ensure employees receive timely payouts and visible recognition from guests — two factors proven to boost morale and reduce the high cost of turnover in hospitality.",
    },
    {
      q: "How does tipkit help in increasing employee motivation?",
      a: " Tipkit simplifies how staff receive and track tips. This transparency builds trust and encourages consistent, high-quality service. Employees who can clearly see the results of their effort tend to feel more valued and engaged in their roles.",
    },
    {
      q: "How tipkit improves guest satisfaction?",
      a: " For guests, Tipkit makes it easy to show appreciation — no cash or app required. This seamless experience encourages more guests to leave a tip, helping staff feel appreciated and motivated while elevating the overall guest experience.",
    },
    {
      q: "How does tipkit enhance employee recognition?",
      a: " Tipkit highlights individual staff contributions through clear digital records, making it easier for managers to recognize great service. Regular acknowledgment leads to stronger team culture and long-term employee satisfaction.",
    },
    {
      q: "How does tipkit increase revenue potential?",
      a: " Hotels using digital tipping tools like Tipkit often see higher overall tip amounts and improved staff retention rates. More motivated employees and satisfied guests translate into stronger performance and a healthier bottom line.\n\nBy adopting Tipkit’s cashless tipping platform, hospitality businesses can build a workplace culture centered on recognition, transparency, and retention — keeping great employees for the long run.",
    },
    {
      q: "What happens if a guest wants to split a tip between staff?",
      a: "Guests can leave a pooled tip that’s distributed evenly among a team, depending on your hotel’s setup.",
    },
    {
      q: "Can digital tips replace traditional cash envelopes?",
      a: "Yes, and they often enhance the guests' experience. Digital tips ensure transparency, reduce handling errors, and make it easier for management to track performance and distribute payouts fairly. With tipkit, staff also are able to watch their increase in tips via their own app and receive positive feedback right from their guests.",
    },
  ],
  technicalSupport: [
    {
      q: "Why isn’t my Tipkit QR code scanning?",
      a: "Check for wifi disruptions, glare, smudges, or damage to the printed QR code. If issues persist, download and reprint the QR code from your dashboard or contact Tipkit support to regenerate a new one.",
    },
    {
      q: "How do I reset my Tipkit password?",
      a: "Go to your Tipkit login page and click “Forgot password.” You’ll receive an email link to securely create a new one.",
    },
    {
      q: "Who can I contact for support?",
      a: "You can reach Tipkit Support at hello@tipkit.co or through the contact form on the dashboard.",
    },
  ],
};

export function FAQDetail() {
  const { id } = useParams();
  const faqs = FAQ_DATA[id] || [];
  const navigate = useNavigate();
  const category = Categories.find((cat) => cat.id === id);

  return (
    <Container>
      <Box sx={{ maxWidth: 900, mx: "auto", pt: 15, pb: 8 }}>
        {category ? (
          <>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: "#034272" }}
                aria-label="Go back"
              >
                <ArrowBackIosIcon />
              </IconButton>
              <Typography
                variant="h5"
                color="#034272"
                fontWeight="medium"
                gutterBottom
              >
                {category.title}
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary" mb={4} pl={6}>
              {category.short}
            </Typography>
          </>
        ) : (
          <Typography variant="h6" gutterBottom>
            FAQ
          </Typography>
        )}

        {faqs.length > 0 ? (
          faqs.map((faq, i) => (
            <Accordion
              elevation={0}
              key={i}
              sx={{
                "& .MuiAccordionSummary-root": {
                  paddingY: "10px !important",
                },
                "& .MuiAccordionDetails-root": {
                  paddingBottom: "25px",
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "15px 0px",
                },
                "& .MuiSvgIcon-root": {
                  color: "#034272",
                },
                "&.Mui-expanded .MuiSvgIcon-root": {
                  color: "#034272",
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium" color="#034272">
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {faq.a.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    <Typography variant="body2">
                      {line} <br />
                    </Typography>
                  </React.Fragment>
                ))}
              </AccordionDetails>
              <Divider />
            </Accordion>
          ))
        ) : (
          <Typography color="text.secondary" mt={3}>
            No FAQs found for this category.
          </Typography>
        )}
      </Box>
    </Container>
  );
}
